import React, { useState, useEffect } from 'react';
import { Download, File, Calendar, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { filesApi } from '../utils/api';
import posthog from 'posthog-js';

const FileList = ({ refreshTrigger }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, [refreshTrigger]);

  const fetchFiles = async () => {
    try {
      const response = await filesApi.getMyUploads();
      setFiles(response.data); // already sorted by server
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateGroup = (dateString) => {
    const fileDate = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Reset hours for comparison
    const fileDateOnly = new Date(fileDate.getFullYear(), fileDate.getMonth(), fileDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    if (fileDateOnly.getTime() === todayOnly.getTime()) {
      return 'today';
    } else if (fileDateOnly.getTime() === yesterdayOnly.getTime()) {
      return 'yesterday';
    } else {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      if (fileDate >= weekAgo) {
        return 'thisWeek';
      }
    }
    // Return actual date for older files
    const day = String(fileDateOnly.getDate()).padStart(2, '0');
    const month = String(fileDateOnly.getMonth() + 1).padStart(2, '0');
    const year = fileDateOnly.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const groupFilesByDate = (files) => {
    const groups = {};

    files.forEach(file => {
      const group = getDateGroup(file.created_at);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(file);
    });

    // Sort groups to ensure proper order: today, yesterday, thisWeek, then dates in descending order
    const sortedGroups = {};
    const order = ['today', 'yesterday', 'thisWeek'];

    // Add special groups first
    order.forEach(key => {
      if (groups[key]) {
        sortedGroups[key] = groups[key];
      }
    });

    // Add date groups in descending order
    Object.keys(groups)
      .filter(key => !order.includes(key))
      .sort((a, b) => {
        // Parse DD.MM.YYYY format
        const [dayA, monthA, yearA] = a.split('.').map(Number);
        const [dayB, monthB, yearB] = b.split('.').map(Number);
        const dateA = new Date(yearA, monthA - 1, dayA);
        const dateB = new Date(yearB, monthB - 1, dayB);
        return dateB - dateA; // Descending order
      })
      .forEach(key => {
        sortedGroups[key] = groups[key];
      });

    return sortedGroups;
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await filesApi.downloadFile(fileId);

      // Create blob link to download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `processed_${filename}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      posthog.capture('file_downloaded', { filename });
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 p-6">
      <h2 className="text-xl font-bold text-neutral-800 mb-6 flex items-center">
        <File className="h-6 w-6 mr-3 text-primary-500" />
        {t('fileList.title')}
      </h2>

      {files.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-neutral-100 p-4 rounded-2xl mx-auto w-fit mb-4">
            <File className="h-12 w-12 text-neutral-400" />
          </div>
          <p className="text-neutral-500 font-medium">{t('fileList.noFiles')}</p>
          <p className="text-neutral-400 text-sm mt-1">{t('fileList.uploadToStart')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupFilesByDate(files)).map(([group, groupFiles]) => (
            groupFiles.length > 0 && (
              <div key={group}>
                <h3 className="text-sm font-semibold text-neutral-600 mb-3 px-2">
                  {['today', 'yesterday', 'thisWeek'].includes(group)
                    ? t(`fileList.dateGroups.${group}`)
                    : group
                  }
                </h3>
                <div className="space-y-4">
                  {groupFiles.map((file) => (
                    <div key={file.id} className="bg-neutral-50/70 border border-neutral-200/50 rounded-xl p-5 transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <div className="bg-primary-100 p-2 rounded-lg mr-3">
                              <File className="h-5 w-5 text-primary-600" />
                            </div>
                            <h3 className="font-semibold text-neutral-800">{file.original_filename}</h3>
                            {file.processing_status === 'completed' && (
                              <CheckCircle className="h-5 w-5 text-success-500 ml-3" />
                            )}
                            {(file.processing_status === 'pending' || file.processing_status === 'processing') && (
                              <Loader className="h-5 w-5 text-blue-500 ml-3 animate-spin" />
                            )}
                            {file.processing_status === 'failed' && (
                              <AlertCircle className="h-5 w-5 text-error-500 ml-3" />
                            )}
                          </div>

                          <div className="flex items-center text-sm text-neutral-600 mb-3 flex-wrap gap-2">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-2" />
                              <span>
                                {new Date(file.created_at).toLocaleDateString()} {new Date(file.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            {file.rows_count != null && file.processing_status === 'completed' && (
                              <>
                                <span>•</span>
                                <span className="bg-success-100 text-success-700 px-2 py-1 rounded-full text-xs font-semibold">
                                  {file.rows_count} {t(file.rows_count === 1 ? 'fileList.rows.single' : 'fileList.rows.multiple')}
                                </span>
                              </>
                            )}
                          </div>

                          <div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              file.processing_status === 'completed'
                                ? 'bg-success-100 text-success-800'
                                : file.processing_status === 'processing'
                                ? 'bg-blue-100 text-blue-800'
                                : file.processing_status === 'pending'
                                ? 'bg-warning-100 text-warning-800'
                                : 'bg-error-100 text-error-800'
                            }`}>
                              {file.processing_status === 'completed' && `✓ ${t('fileList.status.completed')}`}
                              {file.processing_status === 'processing' && `⚙️ ${t('fileList.status.processing')}`}
                              {file.processing_status === 'pending' && `⏳ ${t('fileList.status.pending')}`}
                              {file.processing_status === 'failed' && `✗ ${t('fileList.status.failed')}`}
                            </span>
                            {file.processing_status === 'failed' && file.error_message && (
                              <p className="text-xs text-error-600 mt-2">{file.error_message}</p>
                            )}
                          </div>
                        </div>

                        {file.processing_status === 'completed' && (
                          <button
                            onClick={() => handleDownload(file.id, file.original_filename)}
                            className="ml-4 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium flex items-center text-sm transition-all duration-200"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {t('fileList.download')}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};

export default FileList;